/**
 * useDocumentUpload Hook
 * Orchestrates 2-stage document upload with progress tracking
 */

import { useState, useCallback } from 'react';
import * as prospectMediaService from '../services/prospect-media';
import { getFileExtension } from '../utils/document-validation';
import { getFileNameWithoutExtension } from '../utils/document-utils';
import { retryOperation } from '../utils/retry';
import type { DocumentUploadProgress, DocumentUploadResult } from '../types/prospect-media';

export function useDocumentUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<DocumentUploadProgress>({
    stage: 'requesting_url',
    percent: 0,
    message: 'Preparing upload...',
  });

  const resetProgress = useCallback(() => {
    setProgress({
      stage: 'requesting_url',
      percent: 0,
      message: 'Preparing upload...',
    });
  }, []);

  const uploadDocument = useCallback(async (
    prospectId: string,
    documentFile: File,
    title?: string
  ): Promise<DocumentUploadResult> => {
    const startTime = Date.now();
    setIsUploading(true);

    try {
      // Stage 1: Request upload URL (0-10%)
      setProgress({
        stage: 'requesting_url',
        percent: 0,
        message: 'Requesting upload URL...',
      });

      const extension = getFileExtension(documentFile.name);
      const contentType = documentFile.type;

      // Use provided title or generate from filename
      const documentTitle = title || getFileNameWithoutExtension(documentFile.name);

      const uploadUrlResponse = await retryOperation(() =>
        prospectMediaService.requestDocumentUploadUrl(prospectId, {
          fileExtension: extension,
          contentType,
          fileSizeBytes: documentFile.size,
          title: documentTitle,
          isHandwrittenNote: false,
        })
      );

      console.log('[DocumentUpload] Upload URL received', {
        prospectId,
        mediaId: uploadUrlResponse.mediaId,
        fileName: documentFile.name,
        fileSize: documentFile.size,
        title: documentTitle,
      });

      // Stage 2: Upload document to GCS (10-100%)
      setProgress({
        stage: 'uploading',
        percent: 10,
        message: 'Uploading document...',
      });

      const documentUploadResult = await retryOperation(() =>
        prospectMediaService.uploadDocumentToGCS(
          uploadUrlResponse.uploadUrl,
          documentFile,
          contentType,
          (uploadPercent) => {
            // Map upload progress (0-100%) to our progress range (10-100%)
            const overallPercent = 10 + Math.round(uploadPercent * 0.9);
            setProgress({
              stage: 'uploading',
              percent: overallPercent,
              message: `Uploading document... ${uploadPercent}%`,
            });
          }
        )
      );

      if (!documentUploadResult.success) {
        throw new Error(documentUploadResult.error || 'Document upload failed');
      }

      // Complete
      setProgress({
        stage: 'complete',
        percent: 100,
        message: 'Upload complete',
      });

      const uploadTimeMs = Date.now() - startTime;

      console.log('[DocumentUpload] Upload successful', {
        prospectId,
        mediaId: uploadUrlResponse.mediaId,
        fileName: documentFile.name,
        uploadTimeMs,
      });

      return {
        success: true,
        mediaId: uploadUrlResponse.mediaId,
        gcsUrl: uploadUrlResponse.gcsUrl,
        uploadTimeMs,
      };
    } catch (error) {
      const uploadTimeMs = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('[DocumentUpload] Upload failed', {
        error,
        prospectId,
        fileName: documentFile.name,
        uploadTimeMs,
      });

      return {
        success: false,
        error: errorMessage,
        uploadTimeMs,
      };
    } finally {
      setIsUploading(false);
    }
  }, []);

  return {
    uploadDocument,
    isUploading,
    progress,
    resetProgress,
  };
}
